import React, {useEffect, useState} from 'react';
import Translate, {t} from "../../../../../../../../../components/Translate";
import Input from "../../../../../../../../../components-new/form/Input";
import config from "../../../../../../../../../config";
import {getAiStyle, updateAiStyle} from "../../../../../../../../../services/company/api";
import AiBlock from "./AiBlock";
import AiBlockPlaceholder from "./AiBlockPlaceholder";
import ChoiceButtons from "../../../../../../../../../components-new/form/ChoiceButtons";

// ordered from reserved to warm
const TONES = ['PROFESSIONAL', 'NEUTRAL', 'FRIENDLY'];

const FORMALITIES = ['FORMAL', 'INFORMAL'];

const LENGTHS = ['SHORT', 'NORMAL', 'DETAILED'];

// ordered from the most reserved to the most eager, so the list reads as a scale
const ESCALATIONS = ['NEVER', 'ON_REQUEST_ONLY', 'WHEN_UNSURE', 'UNSURE_OR_OFF_TOPIC'];

const END_CHATS = ['NEVER', 'ON_GOODBYE_ONLY', 'WHEN_DONE'];


const errorKeyOf = (error) =>
    error && error.response && typeof error.response.data === 'string'
        ? error.response.data
        : "somethingWentWrong";

/**
 * Two blocks, because the settings do two different things: the first only
 * changes wording, the second changes when a chat leaves the bot.
 */
const AiStyle = ({client, aiType, user, addNotification}) => {

    // Team AI has its own service and its own agreed behaviour, and reads none
    // of these settings
    const isSupported = aiType === config.AITypes.GPT_AI || aiType === config.AITypes.GEMINI_AI;

    const [style, setStyle] = useState(null);
    const [failed, setFailed] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const [isSaving, setIsSaving] = useState(false);
    const [nameDraft, setNameDraft] = useState('');

    useEffect(() => {
        let active = true;

        if (!isSupported) return;

        setFailed(false);

        getAiStyle(client.id)
            .then(data => {
                if (!active) return;
                setStyle(data);
                setNameDraft(data.botName || '');
            })
            .catch(() => {
                if (active) setFailed(true);
            });

        return () => {
            active = false;
        };
    }, [client.id, aiType, attempt]);

    const save = (patch) => {
        setIsSaving(true);

        updateAiStyle(client.id, patch)
            .then(data => {
                setStyle(data);
                setNameDraft(data.botName || '');
                addNotification("successful", config.notificationLevels.success);
            })
            .catch(error => addNotification(errorKeyOf(error), config.notificationLevels.error))
            .finally(() => setIsSaving(false));
    };

    // the guard sits after the hooks on purpose: returning earlier would change the
    // number of hooks between renders, and React fails the whole screen when the
    // company switches its AI type in the selector above
    if (!isSupported) return null;

    // Not "return null": these settings exist, and a request that failed or is
    // still in flight is not the same thing as a setting the company does not
    // have. Vanishing silently is what made half the panel look absent.
    if (!style) {
        return (
            <AiBlock titleKey="aiStyle">
                <AiBlockPlaceholder failed={failed} onRetry={() => setAttempt(a => a + 1)}/>
            </AiBlock>
        );
    }

    /**
     * One control shape for every setting on this screen.
     *
     * @param explain the wording settings need no caption - their names say what
     *   they do - while the two policies below change what happens to a chat, and
     *   the cost of each choice is not obvious from its label
     */
    const choice = (labelKey, values, current, field, {stacked, explain, lockedTo} = {}) => (
        <div className="ai-style__field">
            <span className="ai-style__label"><Translate text={labelKey}/></span>

            <ChoiceButtons
                value={current}
                stacked={stacked}
                disabled={isSaving}
                onChange={(value) => save({[field]: value})}
                options={values.map(value => {
                    // an option that cannot take effect is offered but not
                    // selectable, and says why on hover rather than going missing
                    const locked = !!lockedTo && value !== lockedTo;

                    return {
                        value,
                        label: <Translate text={labelKey + value}/>,
                        disabled: locked,
                        title: locked ? t({text: 'aiEscalationBlocked', user}) : undefined
                    };
                })}
            />

            {explain && (
                <span className="ai-style__hint">
                    <Translate text={labelKey + current + 'Expl'}/>
                </span>
            )}
        </div>
    );

    return (
        <React.Fragment>
            <AiBlock titleKey="aiStyle">
                <div className="ai-style">
                    <div className="ai-style__grid">
                        {choice('aiTone', TONES, style.tone, 'tone')}
                        {choice('aiFormality', FORMALITIES, style.formality, 'formality')}
                        {choice('aiAnswerLength', LENGTHS, style.answerLength, 'answerLength')}

                        <div className="ai-style__field">
                            <span className="ai-style__label"><Translate text="aiBotName"/></span>
                            <Input
                                style={{maxWidth: '260px'}}
                                maxLength={40}
                                placeholder={t({text: 'aiBotNamePlaceholder', user})}
                                value={nameDraft}
                                onChange={(e) => setNameDraft(e.target.value)}
                                onBlur={() => nameDraft.trim() !== (style.botName || '') && save({botName: nameDraft.trim()})}
                                disabled={isSaving}
                            />
                        </div>
                    </div>
                </div>
            </AiBlock>

            <AiBlock titleKey="aiBehaviour">
                <div className="ai-style">
                    {/* the stored choice is kept as the company's intent; this says
                        whether it is in effect right now */}
                    {!style.operatorsReachable && (
                        <div className="ai-style__warning">
                            <Translate text="aiOperatorsUnreachable"/>
                        </div>
                    )}

                    <div className="ai-style__grid ai-style__grid--wide">
                        {choice('aiEscalation', ESCALATIONS, style.escalationPolicy, 'escalationPolicy',
                            {stacked: true, explain: true,
                             lockedTo: style.operatorsReachable ? null : 'NEVER'})}
                        {choice('aiEndChat', END_CHATS, style.endChatPolicy, 'endChatPolicy',
                            {stacked: true, explain: true})}
                    </div>
                </div>
            </AiBlock>
        </React.Fragment>
    );
};

export default AiStyle;



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/management/clients/new/configurations/components/AI/AiStyle.jsx